// src/try/SampleLibrary.tsx: Add Photos on the test ride (docs/DECISIONS.md "Public demo (try.homebuild.ai)"). Nothing is
// uploaded from the visitor's device: every Add Photos (the phone's tab, the desktop button, Sort, a room, the tour's
// photos stop) opens this sheet of homebuild's own sample photos. Picked photos are copied into the visitor's copy
// with the room the sort would suggest, and Sort opens for them, as after an upload; added from a room, they go
// straight into it. One sheet, mounted once at the app level (App.tsx).
import { useEffect, useState } from "preact/hooks";
import { SAMPLE_PHOTOS, sampleFile } from "../../shared/samples";
import { href, navigate } from "../app/router";
import { api } from "../lib/api";
import { ApiError } from "../lib/http";
import { titleCase } from "../lib/format";
import { getProjectStore, ingestItems } from "../state/project";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Sheet } from "../ui/Sheet";
import { toast } from "../ui/Toasts";
import { isTrial, sampleLibrary } from "./trial";
import "../styles/try.css";

export function SampleLibraryHost() {
  if (!isTrial()) return null;
  return <SampleLibrary />;
}

function SampleLibrary() {
  const target = sampleLibrary.value;
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (target) setPicked([]);
  }, [target?.pid, target?.roomId]);

  const close = () => {
    if (!busy) sampleLibrary.value = null;
  };
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const add = async () => {
    if (!target || picked.length === 0 || busy) return;
    setBusy(true);
    try {
      const res = await api.addSamplePhotos(target.pid, { photos: picked, roomId: target.roomId });
      ingestItems(getProjectStore(target.pid), res.items, res.version);
      sampleLibrary.value = null;
      const n = res.items.length;
      if (target.roomId) {
        toast(`${n === 1 ? "1 photo" : `${n} photos`} added to ${titleCase(target.roomName ?? "the room")}`);
      } else {
        navigate(href("sort", { pid: target.pid }, { batch: res.batch }));
      }
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Those photos didn't add. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const n = picked.length;
  return (
    <Sheet
      open={!!target}
      onClose={close}
      title="Sample Photos"
      class="hb-add-photos try-lib"
      footer={
        <div class="try-lib-foot">
          <span class="try-count">{n === 0 ? "Pick a few" : `${n} picked`}</span>
          <Button size="lg" icon="image-plus" busy={busy} disabled={n === 0} onClick={() => void add()}>
            {n === 0 ? "Add Photos" : n === 1 ? "Add 1 Photo" : `Add ${n} Photos`}
          </Button>
        </div>
      }
    >
      <p class="as-note">Uploads are off in the test ride. Pick from ours.</p>
      <ul class="try-grid" aria-label="Sample photos">
        {SAMPLE_PHOTOS.map((p) => {
          const on = picked.includes(p.id);
          return (
            <li key={p.id}>
              <button type="button" class="try-pick" aria-pressed={on ? "true" : "false"} data-sample={p.id} onClick={() => toggle(p.id)}>
                <img src={sampleFile(p.id, "w480")} alt={p.caption} loading="lazy" width={480} height={360} />
                <span class="try-check" aria-hidden="true">
                  <Icon name="check" />
                </span>
                <span class="try-cap">{p.title}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </Sheet>
  );
}
